/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { RootState } from '../../../store/root.reducers';
import SettingsVideoWindowInteractiveLayer from '../InteractiveLayer/SettingsVideoWindowInteractiveLayer';
import { Mousetrap } from '../../common';
import { settingsVideoWindowSetSelectedOptionAction } from '../../../store/settings/settingsvideowindow/settingsvideowindow.actions';
import { ServicesClientImpl } from '../../../core/impls/ServicesClientImpl';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import {
    settingsVideoWindowDisableGhostMode,
    settingsVideoWindowEnableGhostMode,
} from '../../../store/settings/settingsvideowindow/settingsvideowindow.reducers';
import { BrandingUiUtility } from '../../../core/utils/BrandingUiUtility';
import { SettingsVideoWindowProps } from './SettingsVideoWindowProps';
import { ImageLoader } from '../../ImageLoader';
import { ImageLoaderTypes } from '../../ImageLoader/ImageLoaderTypes';
import { DeviceModel } from '../../../core/device';

import './SettingsVideoWindow.scss';

const SettingsVideoWindow: FunctionComponent<SettingsVideoWindowProps> = ({
    isCursorActive,
    value,
}): JSX.Element => {
    const dispatch = useDispatch();
    // list of selectors
    const { selectedOption, isGhostModeActive } = useSelector(
        (state: RootState) => state.settingsVideoWindow,
    );
    // list of local state
    const [videoWindowOption, setVideoWindowOption] = useState('');
    // list of member variables
    const services = new ServicesClientImpl();
    const replaceValue = {
        MANUFACTURER_NAME: services.getBrandingUiInfoForKey(BrandingUiInfoKeys.MANUFACTURER_NAME),
    };
    const videoWindowPageDescription = BrandingUiUtility.formatDescription(
        Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_PAGE_DESCRIPTION),
        replaceValue,
    );

    useEffect(() => {
        if (isCursorActive) dispatch(settingsVideoWindowEnableGhostMode());
        const isVideoWindowEnabled = DeviceModel.getAppSettings().isVideoWindowEnabled();

        setVideoWindowOption(value);

        if (isVideoWindowEnabled) dispatch(settingsVideoWindowSetSelectedOptionAction(0));
        else dispatch(settingsVideoWindowSetSelectedOptionAction(1));

        return () => {
            dispatch(settingsVideoWindowDisableGhostMode());
        };
    }, []);

    useEffect(() => {
        if (selectedOption === 0) {
            setVideoWindowOption(Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_YES));
        } else {
            setVideoWindowOption(Locale.getString(LocaleKeys.SETTINGS_VIDEO_WINDOW_OPTION_NO));
        }
    }, [selectedOption]);

    return (
        <div className="settings-video-window-container">
            <div className="select-the-text">{videoWindowPageDescription}</div>
            {isCursorActive && <SettingsVideoWindowInteractiveLayer />}
            <Mousetrap
                className={`highlight${
                    isGhostModeActive ? ' ghost' : ''
                } settings-video-window-button`}
                value="settings-video-window-button"
            >
                <ImageLoader imageType={ImageLoaderTypes.LEFT_ARROW_ICON} />
                <div className="select-option-name">{videoWindowOption}</div>
                <ImageLoader imageType={ImageLoaderTypes.RIGHT_ARROW_ICON} />
            </Mousetrap>
        </div>
    );
};

SettingsVideoWindow.propTypes = {
    isCursorActive: PropTypes.bool.isRequired,
    value: PropTypes.string.isRequired,
};

export default SettingsVideoWindow;
